import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowLeft, Send, Package, Lock, Unlock, Archive } from 'lucide-react';
import { cn } from '@/utils/merge';
import { commApi } from './commApi';
import { useCommStore } from './commStore';
import { ROLE_LABELS, STATUS_LABELS, fmtTime, fmtDay } from './format';

// «✓ Διαβάστηκε 14:32» under my latest message once the other side opened it.
// When admins read it (a shop or rider writing to the city's admins), who.
const readLine = receipt => {
	const first = receipt?.readers?.[0];
	if (!first) return null;
	const staff = receipt.readers.filter(r => r.role === 'admin' || r.role === 'master');
	const who = staff.length ? ` από ${staff.map(r => r.name).join(', ')}` : '';
	return `✓ Διαβάστηκε${who} ${fmtTime(first.at)}`;
};

const errorText = res => (typeof res?.error === 'string' ? res.error : 'Κάποιο πρόβλημα προέκυψε');

// One conversation: header (with the order's live details for order chats),
// messages oldest-first with older pages loaded on scrolling up, and the
// composer. Opening it marks everything as read for this user only.
export const ChatPane = ({ conversationId, onBack, compact }) => {
	const [detail, setDetail] = useState(null);
	const [messages, setMessages] = useState([]);
	const [readReceipt, setReadReceipt] = useState(null);
	const [hasMore, setHasMore] = useState(false);
	const [loadingOlder, setLoadingOlder] = useState(false);
	const [text, setText] = useState('');
	const [sending, setSending] = useState(false);
	const listRef = useRef(null);
	const stickToBottom = useRef(true);
	const version = useCommStore(s => s.version);
	const lastUpdate = useCommStore(s => s.lastUpdate);

	const markRead = useCallback(() => commApi.read(conversationId), [conversationId]);

	const fetchLatest = useCallback(
		() => Promise.all([commApi.conversation(conversationId), commApi.messages(conversationId)]),
		[conversationId],
	);

	const applyLatest = useCallback(
		([d, m]) => {
			if (d?.error) {
				setDetail({ error: errorText(d) });
				return;
			}
			setDetail(d);
			if (!m?.error) {
				setMessages(m.messages);
				setReadReceipt(m.readReceipt || null);
				setHasMore(m.hasMore);
				if (m.messages.length) markRead();
			}
		},
		[markRead],
	);

	const loadLatest = async () => applyLatest(await fetchLatest());

	// The pane is keyed by conversation, so this runs once per conversation.
	useEffect(() => {
		let alive = true;
		stickToBottom.current = true;
		fetchLatest().then(result => alive && applyLatest(result));
		return () => {
			alive = false;
		};
	}, [fetchLatest, applyLatest]);

	// A real-time update for this conversation: fetch what is new.
	useEffect(() => {
		if (
			!lastUpdate ||
			lastUpdate.settings ||
			Number(lastUpdate.conversationId) !== Number(conversationId)
		)
			return;
		(async () => {
			const m = await commApi.messages(conversationId);
			if (m?.error) return;
			setReadReceipt(m.readReceipt || null);
			setMessages(prev => {
				const known = new Set(prev.map(x => x.id));
				const fresh = m.messages.filter(x => !known.has(x.id));
				return fresh.length ? [...prev, ...fresh] : prev;
			});
			if (m.messages.some(x => !x.mine)) markRead();
			const d = await commApi.conversation(conversationId);
			if (!d?.error) setDetail(d);
		})();
	}, [version]); // eslint-disable-line react-hooks/exhaustive-deps

	useLayoutEffect(() => {
		const el = listRef.current;
		if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
	}, [messages]);

	const loadOlder = async () => {
		if (!hasMore || loadingOlder || !messages.length) return;
		setLoadingOlder(true);
		const el = listRef.current;
		const heightBefore = el?.scrollHeight || 0;
		const m = await commApi.messages(conversationId, messages[0].id);
		setLoadingOlder(false);
		if (m?.error) return;
		stickToBottom.current = false;
		setMessages(prev => [...m.messages, ...prev]);
		setHasMore(m.hasMore);
		requestAnimationFrame(() => {
			if (el) el.scrollTop = el.scrollHeight - heightBefore;
		});
	};

	const send = async () => {
		const body = text.trim();
		if (!body || sending) return;
		setSending(true);
		const res = await commApi.send(conversationId, body);
		setSending(false);
		if (res?.error) {
			toast.error(errorText(res));
			return;
		}
		setText('');
		stickToBottom.current = true;
		setMessages(prev => (prev.some(x => x.id === res.message.id) ? prev : [...prev, res.message]));
	};

	const changeStatus = async status => {
		const res = await commApi.setStatus(conversationId, status);
		if (res?.error) {
			toast.error(errorText(res));
			return;
		}
		loadLatest();
	};

	if (detail?.error) {
		return (
			<div className='flex flex-1 items-center justify-center p-6 text-slate-500'>
				{detail.error}
			</div>
		);
	}
	const conv = detail?.conversation;
	const order = detail?.order;

	return (
		<div className='flex min-h-0 min-w-0 flex-1 flex-col bg-slate-50'>
			{/* Header */}
			<div className='flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2'>
				{onBack && (
					<button type='button' onClick={onBack} className='p-1 text-slate-500' aria-label='Πίσω'>
						<ArrowLeft size={20} />
					</button>
				)}
				<div className='flex min-w-0 flex-1 flex-col'>
					<span className='truncate text-[15px] font-semibold text-slate-800'>
						{conv?.title || '…'}
					</span>
					<span className='truncate text-[12px] text-slate-500'>
						{conv?.type === 'order'
							? 'Συνομιλία παραγγελίας'
							: (detail?.participants || []).map(p => `${p.name} (${p.roleLabel})`).join(' · ')}
						{conv && conv.status !== 'open' && ` · ${STATUS_LABELS[conv.status]}`}
					</span>
				</div>
				{detail?.canChangeStatus && conv && (
					<div className='flex gap-1'>
						{conv.status === 'open' ? (
							<button
								type='button'
								onClick={() => changeStatus('closed')}
								className='flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[12px] text-slate-700'
								title='Κλείσιμο συνομιλίας'
							>
								<Lock size={14} /> {!compact && 'Κλείσιμο'}
							</button>
						) : (
							<button
								type='button'
								onClick={() => changeStatus('open')}
								className='flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[12px] text-slate-700'
								title='Ξανά άνοιγμα'
							>
								<Unlock size={14} /> {!compact && 'Άνοιγμα'}
							</button>
						)}
						{conv.status !== 'archived' && (
							<button
								type='button'
								onClick={() => changeStatus('archived')}
								className='flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[12px] text-slate-700'
								title='Αρχειοθέτηση'
							>
								<Archive size={14} />
							</button>
						)}
					</div>
				)}
			</div>

			{order && (
				<div className='flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-amber-100 bg-amber-50 px-3 py-1.5 text-[12px] text-amber-900'>
					<span className='flex items-center gap-1 font-semibold'>
						<Package size={14} /> Παραγγελία #{order.friendlyId || order.orderId}
					</span>
					<span>Κατάστημα: {order.shop?.name || '—'}</span>
					<span>Διανομέας: {order.delivery?.name || '—'}</span>
					<span>Κατάσταση: {order.status}</span>
				</div>
			)}

			{/* Messages */}
			<div
				ref={listRef}
				className='flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-3'
				onScroll={e => {
					const el = e.currentTarget;
					stickToBottom.current = el.scrollTop + el.clientHeight > el.scrollHeight - 40;
					if (el.scrollTop < 60) loadOlder();
				}}
			>
				{hasMore && (
					<div className='py-1 text-center text-[12px] text-slate-400'>
						{loadingOlder ? 'Φόρτωση…' : 'Κύλιση προς τα πάνω για παλαιότερα'}
					</div>
				)}
				{messages.map((m, i) => {
					const day = fmtDay(m.created_at);
					const showDay = i === 0 || fmtDay(messages[i - 1].created_at) !== day;
					return (
						<React.Fragment key={m.id}>
							{showDay && <div className='my-2 text-center text-[11px] text-slate-400'>{day}</div>}
							<div className={cn('flex', m.mine ? 'justify-end' : 'justify-start')}>
								<div
									className={cn(
										'max-w-[80%] rounded-2xl px-3 py-1.5 shadow-sm',
										m.mine
											? 'rounded-br-sm bg-[#0f9b9d] text-white'
											: 'rounded-bl-sm bg-white text-slate-800',
									)}
								>
									{!m.mine && (
										<div className='text-[11px] font-semibold text-[#0f9b9d]'>
											{m.sender_name} · {m.senderRoleLabel || ROLE_LABELS[m.sender_role]}
										</div>
									)}
									<div className='text-[14px] break-words whitespace-pre-wrap'>
										{m.status === 'hidden' ? <i>Το μήνυμα αφαιρέθηκε</i> : m.body}
									</div>
									<div
										className={cn(
											'text-right text-[10px]',
											m.mine ? 'text-teal-100' : 'text-slate-400',
										)}
									>
										{fmtTime(m.created_at)}
									</div>
								</div>
							</div>
							{m.mine && readReceipt?.messageId === m.id && (
								<div className='mt-0.5 pr-1 text-right text-[11px] text-slate-500'>
									{readLine(readReceipt)}
								</div>
							)}
						</React.Fragment>
					);
				})}
				{detail && !messages.length && (
					<div className='m-auto text-[13px] text-slate-400'>Γράψτε το πρώτο μήνυμα.</div>
				)}
			</div>

			{/* Composer */}
			{detail?.canReply ? (
				<div className='flex items-end gap-2 border-t border-slate-200 bg-white p-2'>
					<textarea
						rows={1}
						value={text}
						maxLength={4000}
						onChange={e => setText(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								send();
							}
						}}
						placeholder='Γράψτε μήνυμα…'
						className='max-h-32 min-h-10 flex-1 resize-none rounded-2xl border border-slate-300 px-3 py-2 text-[14px] outline-none focus:border-[#0f9b9d]'
					/>
					<button
						type='button'
						onClick={send}
						disabled={!text.trim() || sending}
						className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0f9b9d] text-white disabled:opacity-40'
						aria-label='Αποστολή'
					>
						<Send size={18} />
					</button>
				</div>
			) : (
				detail && (
					<div className='border-t border-slate-200 bg-white p-3 text-center text-[13px] text-slate-500'>
						{conv?.status !== 'open'
							? 'Η συνομιλία είναι κλειστή.'
							: 'Δεν έχετε δικαίωμα να απαντήσετε σε αυτή τη συνομιλία.'}
					</div>
				)
			)}
		</div>
	);
};
